import React, {
    MutableRefObject,
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";
import {first, useAsync2} from "nate-react-api-helpers";
import {Card, Popper, Divider} from "@mui/material";
import {Loader} from "../../../../misc/Loader";
import {css} from "@emotion/css";
import {grey} from "@mui/material/colors";
import {useSnackbar} from "../../../../misc/Snackbar";
import {useSyncedRef} from "../../../../misc/SyncedRef";

type PopoutRef = {
    close(): void;
}

export function SearchWithAutoComplete<T>(props: {
    placeholder?: string;
    autoFocus?: boolean;
    anchor?: any;
    width?: number;
    align?: "right";
    onFocus?(): void;
    onBlur?(): void;
    onRender?(): void;
    initialValue?: string;
    resultsInline?: boolean
    isTableCell?: boolean;
    backgroundColor?: string;
    disabled?: boolean;
    showingPopup?: boolean;
    onSearch(input: {search: string}): Promise<T[]>
    onSearchDeps: any[];
    renderResultRow(input: {
        value: T,
        index: number;
        selected: boolean;
        onSelect(value?: any): Promise<void>
        onFullReset(): void
    }): any;
    renderFooter?(props: {
        search: string
        results: T[];
        onFullReset(): void;
        onRefreshSearch(): void;
    }): any;
    renderMisc?(props: {
        onFullReset(): void;
        onSelect(value: T): Promise<void>
    }): any;
    onSelect(value: T): Promise<void>
}) {
    const [search, setSearch] = useState(props.initialValue || "")
    const [anchorRef, setAnchorRef] = useState<HTMLElement|null>(null)
    const [selected, setSelected] = useState(-1);
    const [focused, setFocused] = useState(false);
    const selectedValueRef = useRef<T|undefined|null>();

    const inputRef = useRef<HTMLInputElement|null>(null);
    const curOnSelect = useSyncedRef(props.onSelect)
    const sn = useSnackbar();
    const popoutRef = useRef<PopoutRef|null>(null);

    const reset = useCallback(() => {
        // @ts-ignore
        inputRef.current?.blur();
        setFocused(false);
        setSearch("");
        popoutRef.current?.close();
    }, [])

    const select = useCallback(async (input: T) => {
        try {
            sn.loading();
            console.log("onSelect", input);
            await curOnSelect.current(input)
            sn.hide();
            reset();
        } catch (e: any) {
            console.error(e);
            sn.error(e.toString());
        }
    }, [curOnSelect, sn, reset]);

    const onBlurRef = useSyncedRef(props.onBlur);
    const [isOpen, setIsOpen] = useState(false);

    const showingPopup = props.showingPopup || isOpen;
    useEffect(() => {
        if(focused) return;
        if(showingPopup) return;

        const tm = setTimeout(() => onBlurRef.current?.(), 500)
        return () => clearTimeout(tm);
    }, [onBlurRef, focused, showingPopup]);

    const hasAutoFocus = useRef(false);

    return (
        <div className={productCss} style={{height: "100%", width: "100%", flex: 1, display: "flex", flexDirection: "column"}} onClick={e => {
            e.stopPropagation(); // for blur detector
        }}>
            <form ref={setAnchorRef} onSubmit={e => {
                e.preventDefault();
            }}>
                <input
                    ref={v => {
                        inputRef.current  = v;
                        if(props.autoFocus && v && !hasAutoFocus.current) {
                            v.focus()
                            hasAutoFocus.current = true
                        }
                    }}
                    disabled={props.disabled}
                    type="text"
                    placeholder={props.placeholder || "Start typing product name or code"}
                    style={{border: "none",
                        width: "100%",
                        height: "100%",
                        padding: 4, paddingLeft: 6, paddingRight: 6,
                        backgroundColor: props.backgroundColor,
                    }}
                    onFocus={e => {
                        e.stopPropagation()
                        if(props.onFocus) props.onFocus()
                        setFocused(true)
                    }}
                    onClick={e => {
                        e.stopPropagation()
                    }}
                    onBlur={() => {
                        setFocused(false)
                    }}
                    onKeyDown={e => {
                        switch(e.key) {
                            case "ArrowDown":
                                setSelected(selected+1);
                                break;
                            case "ArrowUp":
                                if(selected > 0) {
                                    setSelected(selected - 1);
                                }
                                break;
                            case "Enter":
                                const val = selectedValueRef.current;
                                if(!val) return;
                                select(val);
                                break;
                    }
                }} value={search} onChange={e => setSearch(e.target.value)} />
            </form>
            {props.resultsInline && <Divider />}
            <Results<T>
                key="results"
                align={props.align}
                popoutRef={popoutRef}
                selected={selected}
                selectedValue={selectedValueRef}
                anchor={anchorRef}
                resetSelected={() => setSelected(0)}
                onFullReset={reset}
                search={search}
                onRender={props.onRender}
                focused={focused}
                onBlur={() => setSearch("")}
                inline={props.resultsInline}
                onSelect={select}
                onSearch={props.onSearch}
                onIsOpen={setIsOpen}
                onSearchDeps={props.onSearchDeps}
                renderResultRow={props.renderResultRow}
                renderFooter={props.renderFooter}
            />
            {props.renderMisc && props.renderMisc({
                onFullReset: reset,
                onSelect: select,
            })}
        </div>
    )
}


const productCss = css({
    "& input::placeholder": {
        color: grey["500"],
        fontStyle: "italic",
    }
})

export function Results<T>(props: {
    search: string;
    inline?: boolean
    focused: boolean;
    selectedValue: {current: T | null | undefined};
    anchor: HTMLElement | null;
    selected: number;
    popoutRef: MutableRefObject<PopoutRef|null>
    align?: "right";
    resetSelected(): void;
    onBlur(): void;
    onRender?(): void;
    onSelect(value: T): Promise<void>;
    onFullReset(): void;

    renderResultRow(input: {
        value: T,
        index: number;
        selected: boolean;
        onSelect(input?: any): Promise<void>
        onFullReset(): void
    }): any;
    renderFooter?(props: {
        search: string
        results: T[];
        onFullReset(): void;
        onRefreshSearch(): void;
    }): any;

    onSearch(input: {
        search: string;
    }): Promise<T[]>

    onIsOpen(tf: boolean): void;

    onSearchDeps: any[];
}) {
    const resetSelected = useSyncedRef(props.resetSelected);

    const [isOpen, setIsOpen] = useState(props.focused || props.search !== "");
    const shouldShow = props.focused || props.search !== "" || isOpen;
    const shouldShowRef = useSyncedRef(shouldShow);

    const onIsOpen = props.onIsOpen
    useEffect(() => {
        onIsOpen(isOpen);
    }, [isOpen, onIsOpen]);

    const onSearchRef = useSyncedRef(props.onSearch);

    const results = useAsync2(async input => {
        if(!shouldShowRef.current) return [];

        resetSelected.current();
        return await onSearchRef.current(input);

    }, {
        search: props.search,
    }, [props.search, shouldShow, ...props.onSearchDeps]);

    const onRenderRef = useRef(props.onRender);
    onRenderRef.current = props.onRender;

    useEffect(() => {
        const tm = setTimeout(() => {
            onRenderRef.current?.();
        }, 100);

        return () => clearTimeout(tm);
    }, [results.result]);

    props.selectedValue.current = first((results.result||[]).filter((item, index) => index === props.selected), v => true);

    useEffect(() => {
        if(shouldShow && !isOpen) {
            setIsOpen(true);
        }
    }, [shouldShow, isOpen]);

    const onBlur = useSyncedRef(props.onBlur);

    useEffect(() => {
        const click = () => {
            onBlur.current();
            setIsOpen(false);
        };

        window.addEventListener("click", click)

        return () => {
            window.removeEventListener("click", click)
        }
    }, [onBlur]);

    useEffect(() => {
        props.popoutRef.current = {
            close: () => {
                setIsOpen(false)
            },
        }

        return () => {
            props.popoutRef.current = null;
        }
    }, [props.popoutRef]);

    return (
        <AutoPopper inline={props.inline} open={shouldShow} anchorEl={props.anchor}
                placement={props.align === "right" ? "bottom-end": "bottom-start"}>
            <Card elevation={2} style={{fontSize: "0.9rem", height: "100%", flex: 1, display: 'flex', flexDirection: "column"}}
                  onClick={e => {
                        e.stopPropagation()
                    }}>
                <Loader {...results}>
                    {rs => <div style={{overflow: "auto"}}>
                        <table style={{borderCollapse: "collapse", width: "100%"}}>
                        <tbody>
                            {rs.map((v, index) => props.renderResultRow({
                                value: v,
                                index: index,
                                selected: index === props.selected,
                                onFullReset: props.onFullReset,
                                onSelect: (input) => {
                                    if(input) {
                                        return props.onSelect(input);
                                    }

                                    return props.onSelect(v)
                                }
                            }))}
                        </tbody>
                        </table>
                    </div>}
                </Loader>
                {props.renderFooter && <div style={{paddingLeft: 8, paddingRight: 8}}>
                    {props.renderFooter({
                        search: props.search,
                        onFullReset: props.onFullReset,
                        results: (results.result || []) as T[],
                        onRefreshSearch: () => results.reload(),
                    })}
                </div>}
            </Card>
        </AutoPopper>
    )
}

function AutoPopper(props: PropsWithChildren<{
    inline?: boolean;
    open: boolean
    anchorEl: any;
    placement: any;
}>) {
    const {inline, ...otherProps} = props;

    if(inline) {
        return (
            <>{props.children}</>
        )
    }

    return <Popper style={{zIndex: 1400}} {...otherProps}>
        {props.children}
    </Popper>
}